import { Reveal } from "@/components/Reveal";

const slug = (s) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const blockText = (b) => b.runs.map((r) => r.t).join("").trim();

const Runs = ({ runs }) => (
  <>
    {runs.map((r, i) => {
      let node = r.t;
      if (r.b) node = <strong className="font-semibold text-slate-800">{node}</strong>;
      if (r.i) node = <em>{node}</em>;
      return <span key={i}>{node}</span>;
    })}
  </>
);

export const LegalDocument = ({ blocks, testId }) => {
  const title = blocks[0];
  let rest = blocks.slice(1);

  let lastUpdated = null;
  if (rest.length && blockText(rest[0]).startsWith("Last updated")) {
    lastUpdated = rest[0];
    rest = rest.slice(1);
  }

  const items = [];
  rest.forEach((b) => {
    const last = items[items.length - 1];
    if (b.type === "bullet" && last && last.type === "list") {
      last.items.push(b);
    } else if (b.type === "bullet") {
      items.push({ type: "list", items: [b] });
    } else {
      items.push(b);
    }
  });

  const headings = items.filter((b) => b.type === "heading");

  return (
    <div data-testid={`${testId}-page`} className="pt-28 md:pt-36">
      <section className="mx-auto max-w-4xl px-4 pb-16 sm:px-6 md:pb-24 lg:px-8">
        <Reveal>
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-blue-600">
            Legal
          </p>
          <h1
            data-testid={`${testId}-title`}
            className="mt-4 font-display text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl"
          >
            <Runs runs={title.runs} />
          </h1>
          {lastUpdated && (
            <p
              data-testid={`${testId}-last-updated`}
              className="mt-4 text-sm text-slate-500"
            >
              <Runs runs={lastUpdated.runs} />
            </p>
          )}
        </Reveal>

        {headings.length > 0 && (
          <Reveal delay={0.1}>
            <nav
              data-testid={`${testId}-toc`}
              aria-label="On this page"
              className="mt-10 rounded-2xl border border-slate-200/80 bg-slate-50 p-6 sm:p-8"
            >
              <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-blue-600">
                On this page
              </p>
              <ol className="mt-4 grid gap-x-8 gap-y-2 sm:grid-cols-2">
                {headings.map((h) => {
                  const text = blockText(h);
                  return (
                    <li key={text}>
                      <a
                        href={`#${slug(text)}`}
                        data-testid={`${testId}-toc-${slug(text)}`}
                        className="text-sm text-slate-600 transition-colors duration-200 hover:text-blue-600"
                      >
                        {text}
                      </a>
                    </li>
                  );
                })}
              </ol>
            </nav>
          </Reveal>
        )}

        <article data-testid={`${testId}-document`} className="mt-12">
          {items.map((b, i) => {
            if (b.type === "heading") {
              const text = blockText(b);
              return (
                <h2
                  key={i}
                  id={slug(text)}
                  data-testid={`${testId}-section-${slug(text)}`}
                  className="mt-12 scroll-mt-28 font-display text-xl font-bold tracking-tight text-slate-900 sm:text-2xl"
                >
                  <Runs runs={b.runs} />
                </h2>
              );
            }
            if (b.type === "subheading") {
              return (
                <p key={i} className="mt-8 text-base font-semibold text-slate-900">
                  <Runs runs={b.runs} />
                </p>
              );
            }
            if (b.type === "list") {
              return (
                <ul key={i} className="mt-4 list-disc space-y-2 pl-6 text-base leading-relaxed text-slate-600">
                  {b.items.map((li, j) => (
                    <li key={j}>
                      <Runs runs={li.runs} />
                    </li>
                  ))}
                </ul>
              );
            }
            return (
              <p key={i} className="mt-4 text-base leading-relaxed text-slate-600">
                <Runs runs={b.runs} />
              </p>
            );
          })}
        </article>
      </section>
    </div>
  );
};
